import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import {
  ApiError,
  approveAccountDeletionRequest,
  listAccountDeletionRequests,
  rejectAccountDeletionRequest,
  type AccountDeletionRequestStatus,
  type DeletionRequestListItem,
} from "../../lib/api";
import { useConfirm } from "../../components/ConfirmDialog";
import { useAppPreferences } from "../../lib/preferences";
import { getAdminStatusTone } from "./status-tone";

const STATUS_FILTERS: Array<{ label: string; value: AccountDeletionRequestStatus }> = [
  { label: "Pending", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
  { label: "Completed", value: "completed" },
  { label: "All", value: "all" },
];

function formatDateTime(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

/** ``canApprove`` is false for support: deletion is irreversible, so only admins approve it. */
export function AccountDeletionRequestsPanel({ canApprove = true }: { canApprove?: boolean } = {}) {
  const { t } = useAppPreferences();
  const { confirm, dialog } = useConfirm();
  const loadGeneration = useRef(0);
  const actionPending = useRef(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [requests, setRequests] = useState<DeletionRequestListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<AccountDeletionRequestStatus>("pending");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});
  const [processingId, setProcessingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await listAccountDeletionRequests(statusFilter);
      if (generation !== loadGeneration.current) return;
      setRequests(response);
      setAdminNotes((current) => {
        const next = { ...current };
        for (const request of response) {
          if (!(request.id in next)) {
            next[request.id] = request.admin_note ?? "";
          }
        }
        return next;
      });
    } catch (requestError) {
      if (generation !== loadGeneration.current) return;
      setLoadError(
        requestError instanceof ApiError
          ? requestError.detail
          : t("Unable to load account deletion requests."),
      );
    } finally {
      if (generation === loadGeneration.current) setIsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void load();
    return () => { loadGeneration.current += 1; };
  }, [load]);

  const handleApprove = async (request: DeletionRequestListItem) => {
    if (actionPending.current) return;
    actionPending.current = true;
    const confirmed = await confirm({
      title: t("Delete this customer account?"),
      description: t("This permanently deletes {email} and anonymizes their retained booking and payment records. This cannot be undone.", { email: request.user_email }),
      confirmButtonLabel: t("Delete account"),
      cancelButtonLabel: t("Keep request pending"),
    });
    if (!confirmed) { actionPending.current = false; return; }
    const requestId = request.id;
    setProcessingId(requestId);
    setError(null);
    setNotice(null);
    try {
      const updated = await approveAccountDeletionRequest(requestId, adminNotes[requestId]?.trim() || undefined);
      setRequests((current) => current.map((request) => request.id === requestId ? updated : request));
      setNotice(t("Account deleted. Retained booking and payment records have been anonymized."));
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.detail
          : t("Unable to delete the account. Please try again."),
      );
    } finally {
      actionPending.current = false;
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    if (actionPending.current) return;
    if (!adminNotes[requestId]?.trim()) {
      setError(t("An admin note is required to reject a deletion request."));
      return;
    }

    actionPending.current = true;
    setProcessingId(requestId);
    setError(null);
    setNotice(null);
    try {
      const updated = await rejectAccountDeletionRequest(requestId, adminNotes[requestId].trim());
      setRequests((current) => current.map((request) => request.id === requestId ? updated : request));
      setNotice(t("Request rejected."));
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiError
          ? requestError.detail
          : t("Unable to reject deletion request."),
      );
    } finally {
      actionPending.current = false;
      setProcessingId(null);
    }
  };

  return (
    <div className="admin-page admin-deletion mx-auto max-w-5xl space-y-5">
      {dialog}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-text-soft)]">
            {t("Account deletion")}
          </p>
          <h2 className="mt-1 text-xl font-semibold text-[var(--color-text)]">{t("Deletion requests")}</h2>
        </div>
        <label className="block">
          <span className="mb-1 block text-xs text-[var(--color-text-soft)]">{t("Status filter")}</span>
          <select
            disabled={processingId !== null}
            className="ops-input"
            onChange={(event) =>
              setStatusFilter(event.target.value as AccountDeletionRequestStatus)
            }
            value={statusFilter}
          >
            {STATUS_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {t(filter.label)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {(notice || error) && (
        <div className="space-y-2">
          {notice && (
            <div className="flex items-center justify-between rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-sm text-[var(--color-success)]" role="status">
              <span>{notice}</span>
              <button
                aria-label={t("Dismiss success message")}
                className="ops-button"
                data-size="sm"
                data-variant="secondary"
                onClick={() => setNotice(null)}
                type="button"
              >
                ✕
              </button>
            </div>
          )}
          {error && (
            <div className="flex items-center justify-between rounded-lg border border-rose-400/20 bg-rose-400/10 px-4 py-2 text-sm text-[var(--color-error)]" role="alert">
              <span>{error}</span>
              <button
                aria-label={t("Dismiss error message")}
                className="ops-button"
                data-size="sm"
                data-variant="secondary"
                onClick={() => setError(null)}
                type="button"
              >
                ✕
              </button>
            </div>
          )}
        </div>
      )}

      {loadError ? (
        <div role="alert" className="space-y-3">
          <p>{loadError} {t("Your review notes are saved on this page.")}</p>
          <button className="ops-button" onClick={() => void load()} type="button">{t("Retry loading requests")}</button>
        </div>
      ) : isLoading ? (
        <p className="text-sm text-[var(--color-text-soft)]">{t("Loading requests...")}</p>
      ) : requests.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border-sub)] px-5 py-8 text-sm text-[var(--color-text-soft)]">
          {t("No account deletion requests match the selected filter.")}
        </div>
      ) : (
        <div className="grid gap-2">
          {requests.map((request) => {
            const isPending = request.status === "pending";
            const isProcessing = processingId === request.id;

            return (
              <div
                className={`rounded-xl border border-[var(--color-border-sub)] bg-[var(--color-surface)] p-3 ${isPending ? "" : "opacity-60"}`}
                data-status={request.status}
                key={request.id}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="min-w-0 max-w-full truncate text-sm font-semibold text-[var(--color-text)]">
                        {request.user_display_name ?? request.user_email}
                      </h3>
                      <span
                        className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase ${getAdminStatusTone(request.status)}`}
                      >
                        {request.status}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-sm text-[var(--color-text-soft)]">
                      {request.user_email} · {request.user_role}
                    </p>
                    <p className="mt-1 text-xs text-[var(--color-text-soft)]">
                      {t("Requested {date}", { date: formatDateTime(request.requested_at) })}
                    </p>
                  </div>
                  <div className="text-right text-xs text-[var(--color-text-soft)]">
                    <p>{t("ID {id}", { id: request.id.slice(0, 8) })}</p>
                    {request.reviewed_at ? (
                      <p>{t("Reviewed {date}", { date: formatDateTime(request.reviewed_at) })}</p>
                    ) : null}
                    {request.completed_at ? (
                      <p>{t("Completed {date}", { date: formatDateTime(request.completed_at) })}</p>
                    ) : null}
                  </div>
                </div>

                {request.reason ? (
                  <div className="mt-2 rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)]">
                    <span className="text-xs text-[var(--color-text-soft)]">{t("Reason:")}</span> {request.reason}
                  </div>
                ) : null}

                {request.admin_note ? (
                  <div className="mt-2 rounded-lg border border-[var(--color-border-sub)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)]">
                    <span className="text-xs text-[var(--color-text-soft)]">{t("Admin note:")}</span> {request.admin_note}
                  </div>
                ) : null}

                {isPending ? (
                  <div className="mt-3 grid gap-2 sm:flex sm:items-end sm:gap-3">
                    <textarea
                      aria-label={t("Review note for {email}", { email: request.user_email })}
                      disabled={processingId !== null}
                      className="ops-input min-h-[60px] flex-1 text-sm"
                      onChange={(event) =>
                        setAdminNotes((current) => ({
                          ...current,
                          [request.id]: event.target.value,
                        }))
                      }
                      placeholder={t("Admin review note...")}
                      rows={2}
                      value={adminNotes[request.id] ?? ""}
                    />
                    <div className="flex flex-wrap gap-2">
                      {canApprove ? <button
                        className="rounded-lg bg-emerald-500/80 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50"
                        disabled={processingId !== null}
                        onClick={() => void handleApprove(request)}
                        type="button"
                      >
                        {isProcessing ? t("Processing...") : t("Delete account…")}
                      </button> : null}
                      <button
                        className="rounded-lg bg-rose-500/80 px-3 py-2 text-xs font-semibold text-white transition hover:bg-rose-500 disabled:opacity-50"
                        disabled={processingId !== null}
                        onClick={() => void handleReject(request.id)}
                        type="button"
                      >
                        {t("Reject")}
                      </button>
                    </div>
                    {!canApprove ? (
                      <p className="text-xs text-[var(--color-text-soft)]">
                        {t("Only admins can approve a deletion. The note box above is sent only if you reject. To hand this to an admin, leave a note on the player's account.")}{" "}
                        {request.user_id ? <Link className="console-link" to={`/admin/accounts/${request.user_id}`}>{t("Open account")}</Link> : null}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
